<template>
  <div class="login-page">
    <QS-login
      ref="qsLogin"
      :loading="loading"
      :count-number="countNumber"
      :passwordLength="6"
      :system-name="SystemName"
      :loginWithPassword="loginWithPassword"
      :tab-locked="tabLocked"
      @handleSendMsg="handleSendMsg"
      @handleLogin="handleLogin"
      @wxCode="onWxCode"
      @wxError="onWxError"
      @wxSelectAccount="onSelectAccount"
      @wxBack="onWxBack"
      @wxBindSendMsg="onWxBindSendMsg"
      @wxBindSubmit="onWxBindSubmit"
    >
      <template v-slot:promotion>
        <section class="mobile-promotion" aria-labelledby="mobile-promotion-title">
          <h2 id="mobile-promotion-title">车务中心移动版 上线通知</h2>
          <div class="mobile-promotion__description">
            <p>车为家快发车，平移PC端功能，随时随地办公</p>
            <p>在线签约，验车，账单，预警，结算全流程数字化管理</p>
            <p>无纸化办公，信息云上存储</p>
          </div>
          <div class="mobile-promotion__code">
            <img src="~@/assets/img/QRCodeLogin.webp" alt="车务中心移动版微信小程序码">
            <p>微信扫码体验</p>
          </div>
        </section>
      </template>
    </QS-login>
  </div>
</template>

<script>
import request from '@/utils/request'
import { SystemName, interfacePrefix } from '@/config'
import { debounce } from '@/utils'
import md5 from 'blueimp-md5'
import { getCloudConfig } from '@/api/login'
import { getBindUserListByCode } from '@/api/wechatLogin'

let tid = null

export default {
  name: 'Login',
  data() {
    return {
      SystemName,
      countNumber: 0,
      loginForm: {
        username: '',
        password: '',
        captcha: '',
      },
      loading: false,
      redirect: '',
      loginWithPassword: false, // 登录是否需要密码
      // ===== 微信扫码登录 =====
      loginKey: '', // 授权后会话凭据，成功才消费、失败可重试
      boundUsers: [], // 多账号列表（BoundUser[]）
      wxPending: false, // 换登录态进行中
      wxLogged: false, // 已有一路成功写入，后到回调一律丢弃
    }
  },
  computed: {
    // 微信换登录态进行中/已成功时锁定 Tab，禁止来回切换
    tabLocked() {
      return this.wxPending || this.wxLogged
    },
  },
  watch: {
    $route: {
      handler(route) {
        this.redirect = route.query && route.query.redirect
      },
      immediate: true,
    },
  },
  methods: {
    // 统一发码：置 60s 倒计时并请求短信
    startSmsCountdown(postData) {
      if (tid) {
        clearInterval(tid)
      }
      this.countNumber = 60
      request({
        url: `${interfacePrefix}/user/token/sendSms`,
        method: 'post',
        data: postData,
      }).then((res) => {
        // request 在 code!==1 时不 reject 而是 resolve 整个 res 并已弹 msg，此处需显式判成功
        if (!res || res.code !== 1) {
          this.countNumber = 0
          return
        }
        this.$message.success('短信验证码已发送到您的手机，注意查收')
        tid = setInterval(() => {
          if (this.countNumber <= 0 && tid) {
            clearInterval(tid)
          }
          this.countNumber--
        }, 1000)
      }).catch(() => {
        if (tid) {
          clearInterval(tid)
        }
        this.countNumber = 0
      })
    },
    handleSendMsg(val) {
      this.loginForm = val
      const { username, password } = val
      let postData = {
        username: this.loginForm.username.trim(),
      }
      let flag = false
      if (this.loginWithPassword) {
        postData = {
          ...postData,
          password: md5(this.loginForm.password.trim()),
        }
        flag = username && password
      } else {
        flag = !!username
      }
      if (flag) {
        this.startSmsCountdown(postData)
      }
    },
    handleLogin(val) {
      this.loginForm = val
      const { username, password, captcha } = val
      let flag = false
      if (this.loginWithPassword) {
        flag = username && password && captcha
      } else {
        flag = username && captcha
      }
      if (flag) {
        debounce(this.requestLogin(), 500)
      }
    },
    requestLogin() {
      this.loading = true
      this.$store.dispatch('Login', this.loginForm).then(() => {
        this.loading = false
        // 登录成功后重新加载资源，带上租户信息，用于Nginx判断是否根据配置走灰度发布策略
        window.location.reload(true)
        this.$_showMessageAlert() // 登录成功时，显示右下角弹窗
        this.$_showMessageMustRead() // 登录成功时，显示中间弹窗
      }).catch(() => {
        this.loading = false
        // this.$message.error(err.msg)
      })
    },
    // ===== 微信扫码登录编排 =====
    // 收到授权 code → 换取已绑定账号列表 + loginKey，据结果分派
    onWxCode({ code, state }) {
      if (this.wxLogged) return
      this.boundUsers = []
      getBindUserListByCode({ code, state }).then((res) => {
        // request 在 code!==1 时不 reject 而是 resolve 整个 res 并已弹 msg，此处需显式判成功
        if (!res || res.code !== 1) return
        const result = res.data || {}
        this.loginKey = result.loginKey || ''
        const users = result.boundUsers || []
        this.boundUsers = users
        this.$nextTick(() => {
          if (!this.$refs.qsLogin) return
          if (result.needBind || users.length === 0) {
            // 未绑定：二维码原位置切换为绑定表单（原地，无弹窗）
            this.$refs.qsLogin.wxShowBind()
          } else if (users.length === 1) {
            // 已绑定单账号：不切 loading，保持扫码后「登录中请稍候」过渡态直接登录进系统
            // （不显示 loading 动画 / 账号列表）
            this.doWeChatLogin(users[0].userAccount)
          } else {
            // 已绑定多账号：二维码原位置切换为账号列表
            this.$refs.qsLogin.wxShowSelect(users)
          }
        })
      }).catch(() => {
        // 网络异常等：msg 已由 request 弹出，loginKey 未消费可重新扫码
      })
    },
    onWxError() {
      // 出码失败由 WxScanLogin 组件内部展示重试入口，此处无需额外处理
    },
    // 绑定表单发码（仅凭用户名，后端据此找关联手机号）
    onWxBindSendMsg(username) {
      const name = (username || '').trim()
      if (name) {
        this.startSmsCountdown({ username: name })
      }
    },
    // 绑定并登录
    onWxBindSubmit({ username, smsCode }) {
      if (!this.loginKey) {
        this.$message.error('登录会话已失效，请重新扫码')
        return
      }
      this.doWeChatBindLogin(username.trim(), smsCode.trim())
    },
    // 多账号列表手选账号 → 换登录态（切 loading 并显示所选公司名让用户确认）
    onSelectAccount(userAccount) {
      this.doWeChatLogin(userAccount, true)
    },
    // 返回重新扫码：二维码区已回 idle 并重新出码，此处作废本次会话
    onWxBack() {
      if (this.wxLogged) return
      this.loginKey = ''
      this.boundUsers = []
    },
    // showLoading：多账号手选后切 loading 并显示所选公司名让用户确认；
    // 单账号自动登录不切 loading（保持扫码后「登录中请稍候」过渡态），成功直接进系统，避免多一次 loading/列表闪现
    doWeChatLogin(username, showLoading = false) {
      if (this.wxLogged || !this.loginKey) return
      this.wxPending = true
      this.loading = true
      if (showLoading && this.$refs.qsLogin) {
        const acc = this.boundUsers.find((u) => u.userAccount === username)
        const company = acc ? acc.tenantName : ''
        this.$refs.qsLogin.wxShowLoading(company ? `正在登录「${company}」…` : '正在登录…')
      }
      // 用 then(onFulfilled, onRejected) 而非 .then().catch()：
      // 避免登录成功后 onWxLoginSuccess 里 reload/弹窗抛的异常被 catch 误当成登录失败，
      // 从而错误触发 resetWxPending（多账号会再弹一次列表、单账号会报错）
      this.$store.dispatch('WeChatLogin', { username, loginKey: this.loginKey })
        .then(() => this.onWxLoginSuccess(), () => this.resetWxPending())
    },
    doWeChatBindLogin(username, smsCode) {
      if (this.wxLogged || !this.loginKey) return
      this.wxPending = true
      this.loading = true
      // 绑定态原地转 loading，文案区别于普通登录
      if (this.$refs.qsLogin) this.$refs.qsLogin.wxShowLoading('绑定成功，正在登录…')
      // 同 doWeChatLogin：成功回调自己抛的异常不应被当成绑定失败
      this.$store.dispatch('WeChatBindLogin', { username, smsCode, loginKey: this.loginKey })
        .then(() => this.onWxLoginSuccess(), () => this.resetWxBindPending())
    },
    onWxLoginSuccess() {
      // 幂等：首个成功者写入并跳转，后到回调因 wxLogged 直接丢弃。
      // reload 会整页刷新，登录后的公告/必读弹窗由主应用在新页面弹出；
      // 登录页未挂载 $_showMessageAlert/$_showMessageMustRead（调用会报 not a function），此处不调
      this.wxLogged = true
      window.location.reload(true)
    },
    resetWxPending() {
      // 失败不消费 loginKey：解除进行中态，错误 toast 已由 request 弹出
      this.wxPending = false
      this.loading = false
      if (!this.$refs.qsLogin) return
      // 多账号：回列表改选；单账号：回二维码重新扫码（不显示只有 1 项的列表）
      if (this.boundUsers.length > 1) {
        this.$refs.qsLogin.wxShowSelect(this.boundUsers)
      } else {
        this.$refs.qsLogin.refreshWxQr()
      }
    },
    resetWxBindPending() {
      // 绑定失败：解除进行中态，回到绑定表单（保留已填内容），错误 toast 已由 request 弹出
      this.wxPending = false
      this.loading = false
      if (this.$refs.qsLogin) this.$refs.qsLogin.wxShowBind(false)
    },
    getLoginCloudConfig() {
      getCloudConfig({
        configKey: 'loginConfig',
      }).then(({ data }) => {
        this.loginWithPassword = data.loginWithPassword
      })
    },
  },
  created() {
    this.getLoginCloudConfig()
  },
}
</script>

<style scoped>
@font-face {
  font-family: 'Alimama ShuHei Title';
  src: url('~@/assets/fonts/AlimamaShuHeiTitle.ttf') format('truetype');
  font-style: normal;
  font-weight: 700;
  font-display: swap;
}

.login-container{
  background: #212f40 url('~@/assets/img/bg_login_chewu.webp') no-repeat;
  background-size: cover;
}

.mobile-promotion {
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 120px;
  width: 650px;
  color: #fff;
  transform: translateY(-50%);
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.24);
}

.mobile-promotion h2 {
  margin: 0 0 20px;
  font-family: 'Alimama ShuHei Title', sans-serif;
  font-size: 46px;
  line-height: 1.3;
  font-weight: 700;
  letter-spacing: 1px;
}

.mobile-promotion__description {
  display: flex;
  flex-direction: column;
  gap: 20px;
  font-size: 22px;
  line-height: 1.5;
  letter-spacing: 0.5px;
}

.mobile-promotion__description p,
.mobile-promotion__code p {
  margin: 0;
}

.mobile-promotion__code {
  margin-top: 26px;
  width: 172px;
  text-align: center;
}

.mobile-promotion__code img {
  display: block;
  width: 172px;
  height: 172px;
  border-radius: 4px;
}

.mobile-promotion__code p {
  margin-top: 16px;
  font-size: 20px;
  line-height: 1.5;
  letter-spacing: 4px;
  white-space: nowrap;
}

@media screen and (max-width: 1399px) {
  .mobile-promotion {
    width: 560px;
  }

  .mobile-promotion h2 {
    font-size: 38px;
  }

  .mobile-promotion__description {
    font-size: 19px;
  }

  .mobile-promotion__code,
  .mobile-promotion__code img {
    width: 150px;
  }

  .mobile-promotion__code img {
    height: 150px;
  }

  .mobile-promotion__code p {
    font-size: 18px;
  }
}

@media screen and (max-width: 1200px) {
  .mobile-promotion {
    display: none;
  }
}
</style>
